import React, { useEffect, useRef } from "react";
import { Header } from "../../../components/header/Header";
import { createSelector } from "@reduxjs/toolkit";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";
import {
  getUserRequestThunk as onGetUserRequestThunk,
  resetRequestThunk as onResetRequestThunk,
} from "../../../slices/user-requests/thunk";
import toast from "react-hot-toast";
import { getAuthUser } from "../../../utils/GetAuthUser";

const defaultImage = "/default.png";

export const UserRequests = () => {
  
  const fileUrl = process.env.REACT_APP_FILE_URL;
  const dispatch = useDispatch();
  const prevIsSuccessRef = useRef(false);

  // --- Redux selector setup ---
  const selectRequestState = (state) => state.userRequest;
  const userRequestSelector = createSelector(selectRequestState, (user) => ({
    isLoading: user.isLoading,
    userRequestList: user.userRequestList,
    isSuccess: user.isSuccess,
  }));

  const { userRequestList, isLoading, isSuccess } =
    useSelector(userRequestSelector);

  // --- Fetch User Requests ---
  const getUserRequests = () => {
    const user = getAuthUser();
    if (user?.user?.id) {
      dispatch(onGetUserRequestThunk({ artist_id: user.user.id }));
    }
  };

  useEffect(() => {
    getUserRequests();
  }, []);

  // --- Show toast + refresh data on success ---
  useEffect(() => {
    if (!prevIsSuccessRef.current && isSuccess) {
      toast.success("Request updated successfully");
      dispatch(onResetRequestThunk());
      getUserRequests();
    }

    prevIsSuccessRef.current = isSuccess;
  }, [isSuccess, dispatch]);

  // --- Get readable status text ---
  const getStatusText = (status) => {
    if (status == 1) return "Accepted";
    if (status == 2) return "Rejected";
    return "Requested";
  };

  return (
    <>
      {/* Header */}
      <header className="fixed top-0 left-0 md:left-[205px] md:w-[calc(100%-205px)] z-40 bg-white w-full h-14 md:h-[84px] flex items-center justify-center px-4 sm:px-[51px] border-b border-border_color">
        <Header />
      </header>

      <div className="w-full  mx-auto bg-[#F7F7F7] min-h-screen">
        <div className="w-full">
          {/* Page Title */}
          <div className="w-full flex items-center justify-start mt-4">
            <span className="font-bold text-black text-[22px] font-grotesk">
              User Requests
            </span>
          </div>

          {/* Items */}
          <div className="w-full mt-6">
            {isLoading ? (
              <div className="w-full mx-auto grid gap-3 sm:gap-4 grid-cols-1 xs:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">
                {Array.from({ length: 10 }).map((_, idx) => (
                  <div key={idx} className="w-full flex flex-col justify-between relative h-full p-3 border border-border_color rounded-xl animate-pulse bg-white">
                    <div className="w-full h-[175px] rounded-xl overflow-hidden bg-gray-200" />
                    <span className="absolute top-4 right-4 w-20 h-6 bg-gray-200 rounded-full" />
                    <div className="flex flex-col justify-between flex-1 mt-3">
                      <div>
                        <div className="flex items-center justify-between mb-3">
                          <div className="w-[130px] h-8 rounded-3xl bg-gray-200" />
                        </div>
                        <div className="space-y-2">
                          <div className="h-4 bg-gray-200 rounded" />
                          <div className="h-4 bg-gray-200 rounded w-5/6" />
                        </div>
                      </div>
                      <div className="flex items-center justify-center gap-2 w-full mt-4">
                        <div className="h-10 bg-gray-200 rounded-[10px] w-full" />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="w-full mx-auto grid gap-3 sm:gap-4 grid-cols-1 xs:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">
                {userRequestList && userRequestList.length > 0 ? (
                  userRequestList.map((card) => {
                    const contestImage = card.contestImage
                      ? `${fileUrl}${card.contestImage}`
                      : defaultImage;

                    return (
                      <div
                        key={card.id}
                        className="w-full flex flex-col justify-between h-full p-3 border border-border_color rounded-xl relative"
                      >
                        {/* Contest Image */}
                        <div className="w-full h-[175px]">
                          <Link
                            to="#"
                            className="relative h-[175px] rounded overflow-hidden block"
                          >
                            <img
                              alt={card.title || "contest image"}
                              className="object-cover object-center w-full h-full block rounded-xl"
                              src={contestImage}
                              onError={(e) =>
                                (e.currentTarget.src = defaultImage)
                              }
                            />
                          </Link>
                        </div>
                        {/* Status */}
                        <span className="text-sm text-black font-medium font-grotesk absolute top-4 right-4 bg-white px-2 py-1 rounded-full shadow-md">
                          {getStatusText(card.is_accept)}
                        </span>
                        <div className="flex flex-col justify-between flex-1 mt-3">
                          <div>
                            <div className="flex items-center justify-between">
                              {/* Category */}
                              <div className="flex items-center justify-center border border-[#DDDDDD] py-[6px] px-[14px] w-[130px] rounded-3xl">
                                <span className="font-bold text-[11px] text-[#262626] font-grotesk">
                                  {card.title || card.contestCategory}
                                </span>
                              </div>
                            </div>
                            {/* Description */}
                            <p className="text-[#696565] text-sm font-grotesk font-medium line-clamp-2 mt-3">
                              {card.description || card.contestDescription}
                            </p>
                          </div>
                          {/* User Info */}
                          {/* <div className="flex items-center gap-2 mt-3">
                            <span className="text-sm font-medium text-[#262626]">
                              {card.userName}
                            </span>
                          </div> */}

                          {/* Conditional Buttons */}
                          <div className="flex items-center justify-center gap-2 w-full mt-4">
                            <>
                              <Link
                                to={`/artist/request-detail/${card.encryptedId}`}
                                className="w-full text-xs font-bold text-white bg-primary font-grotesk text-center tracking-wide   py-3 px-5 rounded-[10px] whitespace-nowrap"
                              >
                                View Details
                              </Link>
                            </>
                          </div>
                        </div>
                      </div>
                    );
                  })
                ) : (
                  <p className="text-center text-gray-500">No requests found</p>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};
